import { motion, AnimatePresence } from 'motion/react';
import { Menu, X, User as UserIcon, LogOut, Shield, ShoppingBag, ArrowRight, HelpCircle } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useBrand } from '../context/BrandContext';
import { useUser } from '../context/UserContext';
import { useCart } from '../context/CartContext';
import { OptimizedImage } from './OptimizedImage';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const { assets, content, resolveMediaUrl } = useBrand();
  const { user, profile, isAdmin, signIn, signOut } = useUser();
  const { cartCount, setIsCartOpen } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  const getLinkClass = (path: string) => {
    const base = "transition-colors uppercase text-[10px] font-bold tracking-[0.4em]";
    return `${base} ${isActive(path) ? "text-ows-teal" : "text-ows-dark/70 hover:text-ows-teal"}`;
  };

  const getMobileLinkClass = (path: string) => {
    const base = "block py-3 text-2xl font-display font-light uppercase tracking-widest transition-colors";
    return `${base} ${isActive(path) ? "text-ows-teal" : "text-ows-dark hover:text-ows-teal"}`;
  };

  const scrollToTop = (e: React.MouseEvent) => {
    if (window.location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      window.history.pushState(null, '', '/');
      setIsOpen(false);
    }
  };

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    if (window.location.pathname === '/') {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      window.history.pushState(null, '', `/#${id}`);
    } else {
      navigate(`/#${id}`);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
    setIsOpen(false);
  };

  return (
    <div className="fixed top-0 left-0 w-full z-50">
      <nav className="bg-white/70 backdrop-blur-xl border-b border-ows-teal/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex items-center">
              <Link to="/" onClick={scrollToTop} className="flex items-center group">
                <div className="relative">
                  <div className="absolute inset-0 bg-ows-teal/20 blur-2xl rounded-full scale-150 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                  <OptimizedImage 
                    src={resolveMediaUrl(assets.logo)} 
                    alt={content.brandName} 
                    className="h-16 w-16 relative z-10 transition-transform group-hover:scale-110 duration-500" 
                    imgClassName="object-contain"
                    fetchPriority="high"
                    loading="eager"
                    fallback={<span className="text-ows-teal font-display font-bold text-xl uppercase tracking-widest text-center">{content?.brandName ? content.brandName.split(" ").map(w => w[0]).join("") : "OWS"}</span>}
                  />
                </div>
              </Link>
            </div>
            <div className="hidden lg:block">
              <div className="ml-8 flex items-center space-x-8 font-mono">
                <Link to="/syndicate" onClick={() => setIsOpen(false)} className={getLinkClass('/syndicate')}>Membership</Link>
                <Link to="/coaching" onClick={() => setIsOpen(false)} className={getLinkClass('/coaching')}>Book Training</Link>
                <Link to="/expeditions" onClick={() => setIsOpen(false)} className={getLinkClass('/expeditions')}>Expeditions</Link>
                <Link to="/team-building" onClick={() => setIsOpen(false)} className={getLinkClass('/team-building')}>Corporate</Link>
                <Link to="/adventures" onClick={() => setIsOpen(false)} className={getLinkClass('/adventures')}>Mission Logs</Link>
                <Link to="/expedition-cx2" onClick={() => setIsOpen(false)} className={getLinkClass('/expedition-cx2')}>Shop Gear</Link>

                <div className="flex items-center gap-6 border-l border-ows-teal/10 pl-12">
                  <button 
                    onClick={() => setIsCartOpen(true)}
                    className="relative p-2 text-ows-dark/70 hover:text-ows-teal transition-colors"
                  >
                    <ShoppingBag className="h-5 w-5" />
                    {cartCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-ows-orange text-white text-[8px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                        {cartCount}
                      </span>
                    )}
                  </button>

                  {user ? (
                    <div className="flex items-center gap-6">
                      <Link to="/member-portal" className="flex items-center gap-4 group/member">
                        <div className="flex flex-col items-end">
                          <span className="text-[8px] tracking-widest uppercase text-ows-dark/40 group-hover/member:text-ows-teal transition-colors">Member Portal</span>
                          <span className="text-[9px] tracking-widest uppercase text-ows-dark font-medium group-hover/member:text-ows-teal transition-colors">{profile?.displayName || user.email}</span>
                        </div>
                        <div className="w-8 h-8 bg-ows-sand rounded-full flex items-center justify-center group-hover/member:bg-ows-teal transition-all">
                          <UserIcon className="h-4 w-4 text-ows-teal group-hover/member:text-white transition-colors" />
                        </div>
                      </Link>
                      <button 
                        onClick={() => signOut()}
                        className="p-2 text-ows-dark/40 hover:text-red-500 transition-colors"
                        title="Sign Out"
                      >
                        <LogOut className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <Link 
                      to="/login"
                      className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-ows-teal hover:text-ows-teal/80 transition-colors"
                    >
                      <UserIcon className="h-3 w-3" /> Sign In
                    </Link>
                  )}
                  
                  <Link 
                    to="/expedition-cx2#configurator"
                    onClick={() => setIsOpen(false)}
                    className="bg-ows-orange px-10 py-4 rounded-full text-white font-light hover:bg-ows-gold hover:text-ows-dark transition-all uppercase text-[10px] tracking-[0.3em] shadow-xl shadow-ows-orange/20"
                  >
                    Pre-Order OWS EXPEDITION CX2
                  </Link>
                </div>
              </div>
            </div>
            <div className="lg:hidden">
              <button onClick={() => setIsOpen(!isOpen)} className="text-ows-dark hover:text-ows-teal p-2">
                {isOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-ows-teal/5 overflow-hidden"
            >
              <div className="px-6 pt-8 pb-12 space-y-6 max-h-[calc(100vh-6rem)] overflow-y-auto">
                <div className="flex justify-center mb-8">
                  <OptimizedImage 
                    src={resolveMediaUrl(assets.logo)} 
                    alt={content.brandName} 
                    className="h-20 w-20"
                    imgClassName="object-contain"
                    fallback={<span className="text-ows-teal font-display font-bold text-2xl uppercase tracking-widest">{content?.brandName ? content.brandName.split(" ").map(w => w[0]).join("") : "OWS"}</span>}
                  />
                </div>
                <Link to="/syndicate" onClick={() => setIsOpen(false)} className={getMobileLinkClass('/syndicate')}>Membership</Link>
                <Link to="/coaching" onClick={() => setIsOpen(false)} className={getMobileLinkClass('/coaching')}>Book Training</Link>
                <Link to="/expeditions" onClick={() => setIsOpen(false)} className={getMobileLinkClass('/expeditions')}>Expeditions</Link>
                <Link to="/team-building" onClick={() => setIsOpen(false)} className={getMobileLinkClass('/team-building')}>Corporate</Link>
                <Link to="/adventures" onClick={() => setIsOpen(false)} className={getMobileLinkClass('/adventures')}>Mission Logs</Link>
                <Link to="/expedition-cx2" onClick={() => setIsOpen(false)} className={getMobileLinkClass('/expedition-cx2')}>Shop Gear</Link>
                
                {!isAdmin && (
                  <Link 
                    to="/admin-login" 
                    onClick={() => setIsOpen(false)} 
                    className="flex items-center justify-between px-6 py-5 bg-ows-dark text-white rounded-[2rem] group mt-8 shadow-xl"
                  >
                    <div className="flex flex-col">
                      <span className="text-[10px] tracking-[0.3em] uppercase text-ows-teal mb-1">Access Channel</span>
                      <span className="text-sm tracking-widest uppercase font-bold">Admin Portal</span>
                    </div>
                    <Shield className="h-5 w-5 text-ows-teal group-hover:scale-110 transition-transform" />
                  </Link>
                )}
                
                <button 
                  onClick={() => {
                    setIsCartOpen(true);
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center justify-between py-4 text-2xl font-display font-light text-ows-dark uppercase tracking-widest border-t border-ows-teal/5 mt-4"
                >
                  <span>Your Bag</span>
                  <div className="flex items-center gap-3">
                    <ShoppingBag className="h-6 w-6 text-ows-teal" />
                    {cartCount > 0 && (
                      <span className="bg-ows-orange text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">
                        {cartCount}
                      </span>
                    )}
                  </div>
                </button>

                <div className="pt-8 border-t border-ows-teal/10 space-y-4 pb-12">
                  {user ? (
                    <div className="space-y-4">
                      <Link 
                        to="/member-portal" 
                        onClick={() => setIsOpen(false)}
                        className="flex items-center justify-between px-6 py-5 bg-ows-teal text-white rounded-[2rem] group shadow-xl"
                      >
                        <div className="flex flex-col">
                          <span className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-1">Authenticated</span>
                          <span className="text-sm tracking-widest uppercase font-bold">{profile?.displayName || user.email}</span>
                        </div>
                        <div className="flex flex-col items-end">
                          <span className="text-[8px] tracking-[0.3em] uppercase text-ows-sand mb-1 font-mono">Member Portal</span>
                          <ArrowRight className="h-5 w-5 text-white group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                      <button 
                        onClick={() => {
                          signOut();
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center justify-center gap-3 px-6 py-5 bg-red-50 text-red-500 rounded-[2rem] text-[10px] font-bold uppercase tracking-[0.4em]"
                      >
                        <LogOut className="h-4 w-4" /> Terminate Session
                      </button>
                    </div>
                  ) : (
                    <Link 
                      to="/login"
                      onClick={() => setIsOpen(false)}
                      className="w-full flex items-center justify-center gap-3 px-6 py-6 bg-ows-teal/10 text-ows-teal rounded-full text-sm font-bold uppercase tracking-[0.3em] transition-all"
                    >
                      <UserIcon className="h-5 w-5" /> Sign In
                    </Link>
                  )}

                  <Link 
                    to="/equipment/endurance#configurator" 
                    onClick={() => setIsOpen(false)} 
                    className="block w-full bg-ows-orange text-white text-center py-6 rounded-full font-bold uppercase tracking-[0.3em] text-sm shadow-2xl shadow-ows-orange/20"
                  >
                    Pre-Order OWS EXPEDITION CX2
                  </Link>

                  <a 
                    href="/#ows-faq-section"
                    onClick={(e) => handleAnchorClick(e, 'ows-faq-section')}
                    className="flex items-center justify-center gap-2 w-full text-center py-5 rounded-full border border-ows-teal/25 text-ows-teal hover:bg-ows-teal/5 font-bold uppercase tracking-[0.3em] text-[10px] transition-all font-mono"
                  >
                    <HelpCircle className="h-4 w-4" />
                    Squad FAQ Intel
                  </a>
                  
                  {isAdmin && (
                    <Link 
                      to="/admin" 
                      onClick={() => setIsOpen(false)}
                      className="w-full bg-ows-dark text-white py-6 rounded-full font-bold uppercase tracking-[0.3em] text-sm flex items-center justify-center gap-3"
                    >
                      <Shield className="h-4 w-4 text-ows-teal" />
                      Admin Dashboard
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </div>
  );
}
